import React, { useState } from "react";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { FaGlobe, FaLaptopCode, FaMobileAlt, FaShoppingCart, FaCheck, FaArrowRight, FaArrowLeft } from "react-icons/fa";
import "./Devis.css";

// ---- Donnees de configuration ----

const PROJECT_TYPES = [
  { id: "site-vitrine", label: "Site vitrine / Landing page", icon: FaGlobe, desc: "Presence en ligne, pages de vente, portfolio" },
  { id: "app-web", label: "Application web", icon: FaLaptopCode, desc: "Espace client, back-office, gestion interne" },
  { id: "app-mobile", label: "Application mobile", icon: FaMobileAlt, desc: "iOS et Android avec React Native" },
  { id: "e-commerce", label: "Boutique e-commerce", icon: FaShoppingCart, desc: "Vente en ligne avec Orange Money et Wave" },
];

const FEATURES_BY_TYPE = {
  "site-vitrine": [
    { id: "blog", label: "Blog / Actualites" },
    { id: "espace-client", label: "Espace client / Connexion" },
    { id: "galerie", label: "Galerie photos" },
    { id: "reseaux-sociaux", label: "Integration reseaux sociaux" },
    { id: "multilingue", label: "Site bilingue (francais + anglais)" },
  ],
  "app-web": [
    { id: "dashboard-admin", label: "Tableau de bord administrateur" },
    { id: "gestion-users", label: "Gestion des utilisateurs et roles" },
    { id: "api-externe", label: "Integration API externe" },
    { id: "notifs-email", label: "Notifications par email" },
    { id: "stats", label: "Rapports et statistiques" },
    { id: "paiement-mobile", label: "Paiement Orange Money / Wave" },
  ],
  "app-mobile": [
    { id: "notifs-push", label: "Notifications push" },
    { id: "paiement-mobile", label: "Paiement Orange Money / Wave" },
    { id: "geoloc", label: "Geolocalisation / Carte" },
    { id: "hors-ligne", label: "Mode hors-ligne" },
    { id: "camera", label: "Camera / Upload photos" },
    { id: "dashboard-admin", label: "Interface admin web" },
  ],
  "e-commerce": [
    { id: "stocks", label: "Gestion des stocks" },
    { id: "historique-commandes", label: "Espace client (historique commandes)" },
    { id: "livraison", label: "Gestion des livraisons" },
    { id: "codes-promo", label: "Codes promo / reductions" },
    { id: "multivendeur", label: "Multi-vendeurs (marketplace)" },
    { id: "notifs-email", label: "Notifications email automatiques" },
  ],
};

const BUDGETS = [
  { id: "moins-200k", label: "Moins de 200 000 FCFA" },
  { id: "200k-500k", label: "200 000 – 500 000 FCFA" },
  { id: "500k-1m", label: "500 000 – 1 000 000 FCFA" },
  { id: "plus-1m", label: "Plus de 1 000 000 FCFA" },
  { id: "sais-pas", label: "Je ne sais pas encore" },
];

const DELAYS = [
  { id: "urgent", label: "Urgent (moins de 2 semaines)" },
  { id: "1-2-mois", label: "1 a 2 mois" },
  { id: "plus-2-mois", label: "Plus de 2 mois" },
  { id: "pas-contrainte", label: "Pas de contrainte" },
];

// ---- Calcul du prix ----

const BASE_PRICES = {
  "site-vitrine": { min: 150000, max: 300000, delay: "7 a 14 jours" },
  "app-web":      { min: 400000, max: 800000, delay: "3 a 6 semaines" },
  "app-mobile":   { min: 500000, max: 1000000, delay: "4 a 8 semaines" },
  "e-commerce":   { min: 300000, max: 600000, delay: "2 a 4 semaines" },
};

const FEATURE_PRICES = {
  "blog":                  { min: 40000,  max: 80000  },
  "espace-client":         { min: 80000,  max: 150000 },
  "galerie":               { min: 30000,  max: 60000  },
  "reseaux-sociaux":       { min: 20000,  max: 40000  },
  "multilingue":           { min: 60000,  max: 100000 },
  "dashboard-admin":       { min: 80000,  max: 150000 },
  "gestion-users":         { min: 60000,  max: 120000 },
  "api-externe":           { min: 80000,  max: 150000 },
  "notifs-email":          { min: 40000,  max: 80000  },
  "stats":                 { min: 60000,  max: 120000 },
  "paiement-mobile":       { min: 80000,  max: 120000 },
  "notifs-push":           { min: 80000,  max: 150000 },
  "geoloc":                { min: 100000, max: 200000 },
  "hors-ligne":            { min: 60000,  max: 120000 },
  "camera":                { min: 40000,  max: 80000  },
  "stocks":                { min: 60000,  max: 120000 },
  "historique-commandes":  { min: 40000,  max: 80000  },
  "livraison":             { min: 80000,  max: 150000 },
  "codes-promo":           { min: 40000,  max: 80000  },
  "multivendeur":          { min: 200000, max: 400000 },
};

function fmt(n) {
  return n.toLocaleString("fr-FR") + " FCFA";
}

function calcEstimate(projectType, features, urgency) {
  const base = BASE_PRICES[projectType] || BASE_PRICES["site-vitrine"];
  let min = base.min;
  let max = base.max;
  features.forEach(fid => {
    const p = FEATURE_PRICES[fid];
    if (p) { min += p.min; max += p.max; }
  });
  if (urgency === "urgent") { min = Math.round(min * 1.3); max = Math.round(max * 1.3); }
  return { min, max, delay: base.delay };
}

// ---- Barre de progression ----

const STEP_LABELS = ["Type de projet", "Fonctionnalites", "Budget & Delai", "Coordonnees"];

function ProgressBar({ step }) {
  return (
    <div className="dv-progress">
      {STEP_LABELS.map((label, i) => (
        <div key={i} className={"dv-step-ind" + (i + 1 <= step ? " active" : "") + (i + 1 === step ? " current" : "")}>
          <div className="dv-step-dot">
            {i + 1 < step ? <FaCheck size={10} /> : i + 1}
          </div>
          <span className="dv-step-label">{label}</span>
          {i < STEP_LABELS.length - 1 && <div className={"dv-step-line" + (i + 1 < step ? " done" : "")} />}
        </div>
      ))}
    </div>
  );
}

// ---- Composant principal ----

const EMPTY = { projectType: "", features: [], budget: "", urgency: "", name: "", company: "", email: "", phone: "", description: "" };

export default function Devis() {
  const [step, setStep] = useState(1);
  const [form, setForm] = useState(EMPTY);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [estimate, setEstimate] = useState(null);
  const [error, setError] = useState("");

  function selectType(id) { setForm(f => ({ ...f, projectType: id, features: [] })); }
  function toggleFeature(id) {
    setForm(f => ({
      ...f,
      features: f.features.includes(id) ? f.features.filter(x => x !== id) : [...f.features, id],
    }));
  }
  function handleChange(e) { setForm(f => ({ ...f, [e.target.name]: e.target.value })); }
  function next() { setStep(s => s + 1); window.scrollTo(0, 0); }
  function back() { setStep(s => s - 1); window.scrollTo(0, 0); }

  async function handleSubmit(e) {
    e.preventDefault();
    setSubmitting(true);
    setError("");

    const est = calcEstimate(form.projectType, form.features, form.urgency);
    setEstimate(est);

    const typeLabel = PROJECT_TYPES.find(t => t.id === form.projectType)?.label || form.projectType;
    const featureLabels = form.features.map(fid => (FEATURES_BY_TYPE[form.projectType] || []).find(f => f.id === fid)?.label || fid);
    const budgetLabel   = BUDGETS.find(b => b.id === form.budget)?.label || form.budget;
    const urgencyLabel  = DELAYS.find(d => d.id === form.urgency)?.label || form.urgency;

    const needsText = [
      "[DEVIS] " + typeLabel,
      "",
      featureLabels.length > 0
        ? "Fonctionnalites :\n" + featureLabels.map(l => "- " + l).join("\n")
        : "Pas de fonctionnalite specifique selectionnee.",
      "",
      "Budget souhaite : " + budgetLabel,
      "Delai souhaite  : " + urgencyLabel,
      "",
      "Estimation auto : " + fmt(est.min) + " - " + fmt(est.max),
      "",
      form.description ? "Notes : " + form.description : "",
    ].filter(Boolean).join("\n");

    try {
      const base = process.env.REACT_APP_PUBLICATION_SERVICE || "http://localhost:4300";
      const res = await fetch(base + "/contacts", {
        method: "POST",
        headers: { "Content-Type": "application/json", Accept: "application/json" },
        body: JSON.stringify({ type: "devis", name: form.name, company: form.company, email: form.email, phone: form.phone, needs: needsText }),
      });
      const json = await res.json().catch(() => null);
      if (!res.ok || (json && json.success === false)) throw new Error(json?.error?.message || "Erreur " + res.status);
      setSubmitted(true);
    } catch (err) {
      setError(err.message || "Erreur inconnue. Reessayez ou contactez-nous directement.");
    } finally {
      setSubmitting(false);
    }
  }

  // ---- Ecran de confirmation ----
  if (submitted && estimate) {
    return (
      <div className="dv-page">
        <Helmet><title>Devis envoye — NEXUS SOLUTIONS</title></Helmet>
        <div className="dv-success">
          <div className="dv-success-icon"><FaCheck size={30} /></div>
          <h1>Demande envoyee !</h1>
          <p>Nous vous contacterons sous <strong>24h</strong> pour confirmer les details et affiner le devis.</p>
          <div className="dv-estimate-card">
            <div className="dv-estimate-title">Estimation preliminaire</div>
            <div className="dv-estimate-range">{fmt(estimate.min)} — {fmt(estimate.max)}</div>
            <div className="dv-estimate-delay">Delai indicatif : {estimate.delay}</div>
            <p className="dv-estimate-note">
              Ce montant est base sur vos selections. Le devis definitif sera confirme apres notre echange.
            </p>
          </div>
          <div className="dv-success-actions">
            <Link to="/" className="dv-home-btn">Retour a l'accueil</Link>
            <a
              href="mailto:contact@nxs-solutions.com"
              className="dv-wa-btn"
            >
              Nous ecrire par email
            </a>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="dv-page">
      <Helmet>
        <title>Devis gratuit — NEXUS SOLUTIONS</title>
        <meta name="description" content="Obtenez une estimation gratuite pour votre projet digital en quelques etapes." />
      </Helmet>

      <div className="dv-hero">
        <h1>Demande de devis</h1>
        <p>4 etapes rapides — estimation immediate — reponse sous 24h.</p>
      </div>

      <div className="dv-card">
        <ProgressBar step={step} />

        {/* ETAPE 1 — Type de projet */}
        {step === 1 && (
          <div className="dv-step">
            <h2>Quel type de projet ?</h2>
            <div className="dv-type-grid">
              {PROJECT_TYPES.map(t => {
                const Icon = t.icon;
                const sel = form.projectType === t.id;
                return (
                  <button key={t.id} type="button" className={"dv-type-card" + (sel ? " selected" : "")} onClick={() => selectType(t.id)}>
                    {sel && <div className="dv-type-check"><FaCheck size={11} /></div>}
                    <div className="dv-type-icon"><Icon size={30} /></div>
                    <div className="dv-type-name">{t.label}</div>
                    <div className="dv-type-desc">{t.desc}</div>
                  </button>
                );
              })}
            </div>
            <div className="dv-nav">
              <span />
              <button className="dv-btn-next" onClick={next} disabled={!form.projectType}>
                Suivant <FaArrowRight size={13} />
              </button>
            </div>
          </div>
        )}

        {/* ETAPE 2 — Fonctionnalites */}
        {step === 2 && (
          <div className="dv-step">
            <h2>Quelles fonctionnalites ?</h2>
            <p className="dv-hint">Selectionnez tout ce qui vous interesse. Vous pouvez en ajouter d'autres plus tard.</p>
            <div className="dv-chips">
              {(FEATURES_BY_TYPE[form.projectType] || []).map(f => {
                const checked = form.features.includes(f.id);
                return (
                  <button key={f.id} type="button" className={"dv-chip" + (checked ? " selected" : "")} onClick={() => toggleFeature(f.id)}>
                    {checked && <FaCheck size={11} style={{ marginRight: 6, flexShrink: 0 }} />}
                    {f.label}
                  </button>
                );
              })}
            </div>
            <div className="dv-nav">
              <button className="dv-btn-back" onClick={back}><FaArrowLeft size={13} /> Retour</button>
              <button className="dv-btn-next" onClick={next}>Suivant <FaArrowRight size={13} /></button>
            </div>
          </div>
        )}

        {/* ETAPE 3 — Budget & Delai */}
        {step === 3 && (
          <div className="dv-step">
            <h2>Budget et delai</h2>
            <div className="dv-section">
              <h3>Budget approximatif</h3>
              <div className="dv-radio-group">
                {BUDGETS.map(b => (
                  <label key={b.id} className={"dv-radio-card" + (form.budget === b.id ? " selected" : "")}>
                    <input type="radio" name="budget" value={b.id} checked={form.budget === b.id} onChange={handleChange} />
                    {b.label}
                  </label>
                ))}
              </div>
            </div>
            <div className="dv-section">
              <h3>Quand souhaitez-vous lancer ?</h3>
              <div className="dv-radio-group">
                {DELAYS.map(d => (
                  <label key={d.id} className={"dv-radio-card" + (form.urgency === d.id ? " selected" : "")}>
                    <input type="radio" name="urgency" value={d.id} checked={form.urgency === d.id} onChange={handleChange} />
                    {d.label}
                  </label>
                ))}
              </div>
            </div>
            <div className="dv-nav">
              <button className="dv-btn-back" onClick={back}><FaArrowLeft size={13} /> Retour</button>
              <button className="dv-btn-next" onClick={next} disabled={!form.budget || !form.urgency}>
                Suivant <FaArrowRight size={13} />
              </button>
            </div>
          </div>
        )}

        {/* ETAPE 4 — Coordonnees */}
        {step === 4 && (
          <form className="dv-step" onSubmit={handleSubmit}>
            <h2>Vos coordonnees</h2>
            {error && <div className="dv-error">{error}</div>}
            <div className="dv-form-row">
              <label>Nom *<input name="name" value={form.name} onChange={handleChange} placeholder="Votre nom" required /></label>
              <label>Entreprise<input name="company" value={form.company} onChange={handleChange} placeholder="Votre entreprise" /></label>
            </div>
            <div className="dv-form-row">
              <label>Email *<input name="email" type="email" value={form.email} onChange={handleChange} placeholder="adresse@exemple.com" required /></label>
              <label>Telephone *<input name="phone" value={form.phone} onChange={handleChange} placeholder="+225 00 00 00 00" required /></label>
            </div>
            <label className="dv-full">
              Notes supplementaires (optionnel)
              <textarea name="description" value={form.description} onChange={handleChange} placeholder="Decrivez votre projet, contraintes specifiques, references..." rows={4} />
            </label>
            <div className="dv-nav">
              <button type="button" className="dv-btn-back" onClick={back}><FaArrowLeft size={13} /> Retour</button>
              <button type="submit" className="dv-btn-next" disabled={submitting || !form.name || !form.email || !form.phone}>
                {submitting ? "Envoi..." : "Envoyer ma demande"}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}
